import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, AlertTriangle, Clock, CheckCircle, 
  ChevronRight, RefreshCw, Users, 
  Building, FileText, DollarSign, CreditCard,
  AlertCircle, TrendingUp, TrendingDown
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const categoryIcons = {
  employee: Users,
  client: Users,
  visit: FileText,
  billing: DollarSign,
  payroll: CreditCard,
  agency: Building
};

// Status colors
const statusColors = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  overdue: 'bg-orange-100 text-orange-800 border-orange-200',
  due_soon: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  compliant: 'bg-green-100 text-green-800 border-green-200',
  acknowledged: 'bg-blue-100 text-blue-800 border-blue-200',
  resolved: 'bg-gray-100 text-gray-800 border-gray-200'
};

// Risk colors
const riskColors = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500'
};

// StatCard Component
function StatCard({ title, value, icon: Icon, color, onClick }) {
  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>{value}</p>
          </div>
          <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// CategoryCard Component
function CategoryCard({ category, isSelected, onSelect }) {
  const Icon = categoryIcons[category.category] || Shield;
  
  return (
    <Card 
      className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
      onClick={onSelect}
      data-testid={`category-${category.category}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-gray-500" />
            <span className="font-medium capitalize">{category.category}</span>
          </div>
          <Badge variant={category.compliance_rate >= 80 ? 'success' : category.compliance_rate >= 50 ? 'warning' : 'destructive'}>
            {category.compliance_rate.toFixed(0)}%
          </Badge>
        </div>
        <div className="flex justify-between text-sm text-gray-500">
          <span>{category.compliant_count} compliant</span>
          <span>{category.total_items} total</span>
        </div>
        {category.critical_count > 0 && (
          <div className="mt-2 text-xs text-red-600">
            {category.critical_count} critical
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ComplianceItemCard Component
function ComplianceItemCard({ item, onAcknowledge, onResolve, onViewDetails }) {
  const Icon = categoryIcons[item.category] || Shield;
  
  return (
    <Card className="mb-3" data-testid={`compliance-item-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {/* Risk indicator */}
            <div className={`w-1 h-full min-h-[60px] rounded-full ${riskColors[item.risk_level]}`} />
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500 capitalize">{item.category}</span>
                <span className="text-gray-300">•</span>
                <span className="text-xs text-gray-500 capitalize">{item.subcategory.replace(/_/g, ' ')}</span>
              </div>
              
              <h4 className="font-medium text-gray-900">{item.title}</h4>
              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              
              {item.entity_name && (
                <p className="text-sm text-gray-500 mt-1">
                  Entity: <span className="font-medium">{item.entity_name}</span>
                </p>
              )}
              
              <div className="flex items-center gap-4 mt-2">
                <Badge className={statusColors[item.status]}>
                  {item.status.replace(/_/g, ' ')}
                </Badge>
                
                {item.due_date && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {item.days_remaining !== null ? (
                      item.days_remaining < 0 
                        ? `${Math.abs(item.days_remaining)} days overdue`
                        : `${item.days_remaining} days remaining`
                    ) : item.due_date}
                  </span>
                )}
                
                {item.responsible_person_name && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {item.responsible_person_name}
                  </span>
                )}
              </div>
              
              <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                <p className="text-gray-600">
                  <strong>Action:</strong> {item.required_action}
                </p>
                <p className="text-gray-500 mt-1">
                  <strong>Next Step:</strong> {item.suggested_next_step}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-2 ml-4">
            {item.can_acknowledge && item.status !== 'acknowledged' && item.status !== 'resolved' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onAcknowledge(item)}
                data-testid={`acknowledge-${item.id}`}
              >
                Acknowledge
              </Button>
            )}
            {item.source_service === 'exception_center' && item.status !== 'resolved' && (
              <Button 
                variant="default" 
                size="sm"
                onClick={() => onResolve(item)}
                data-testid={`resolve-${item.id}`}
              >
                Resolve
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onViewDetails(item)}
            >
              View Details
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ComplianceDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/compliance/dashboard`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/compliance/items?limit=100`;
      
      if (activeTab === 'critical') {
        url = `${API_URL}/api/compliance/items/critical?limit=50`;
      } else if (activeTab === 'overdue') {
        url = `${API_URL}/api/compliance/items/overdue?limit=50`;
      } else if (activeTab === 'due_soon') {
        url = `${API_URL}/api/compliance/items/due-soon?limit=50`;
      }
      
      if (categoryFilter) {
        url += `&category=${categoryFilter}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, categoryFilter]);

  useEffect(() => {
    fetchDashboard();
    fetchItems();
  }, [fetchDashboard, fetchItems]);

  const handleAcknowledge = async (item) => {
    try {
      const response = await fetch(`${API_URL}/api/compliance/items/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_service: item.source_service,
          source_id: item.source_record_id,
          notes: 'Acknowledged via Compliance Center'
        })
      });
      
      if (response.ok) {
        toast.success('Item acknowledged');
        fetchDashboard();
        fetchItems();
      } else {
        toast.error('Failed to acknowledge item');
      }
    } catch (error) {
      toast.error('Error acknowledging item');
    }
  };

  const handleResolve = async (item) => {
    try {
      const response = await fetch(`${API_URL}/api/compliance/items/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_service: item.source_service,
          source_id: item.source_record_id,
          resolution_notes: 'Resolved via Compliance Center'
        })
      });
      
      if (response.ok) {
        toast.success('Item resolved');
        fetchDashboard();
        fetchItems();
      } else {
        toast.error('Failed to resolve item');
      }
    } catch (error) {
      toast.error('Error resolving item');
    }
  };

  const handleViewDetails = (item) => {
    if (item.entity_type === 'employee') {
      navigate(`/employee/${item.entity_id}`);
    } else if (item.entity_type === 'client') {
      navigate(`/clients/${item.entity_id}`);
    }
  };

  const handleRefresh = () => {
    fetchDashboard();
    fetchItems();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="compliance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7" />
            Compliance Center
          </h1>
          <p className="text-gray-500 mt-1">
            Operational health dashboard — monitor compliance across your agency
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Critical"
            value={dashboard.critical_count}
            icon={AlertCircle}
            color="text-red-600"
            onClick={() => setActiveTab('critical')}
          />
          <StatCard
            title="Overdue"
            value={dashboard.overdue_count}
            icon={AlertTriangle}
            color="text-orange-600"
            onClick={() => setActiveTab('overdue')}
          />
          <StatCard
            title="Due Soon"
            value={dashboard.due_soon_count}
            icon={Clock}
            color="text-yellow-600"
            onClick={() => setActiveTab('due_soon')}
          />
          <StatCard
            title="Compliant"
            value={dashboard.compliant_count}
            icon={CheckCircle}
            color="text-green-600"
            onClick={() => setActiveTab('all')}
          />
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Compliance Rate</p>
                  <p className={`text-3xl font-bold ${
                    dashboard.overall_compliance_rate >= 80 ? 'text-green-600' :
                    dashboard.overall_compliance_rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {dashboard.overall_compliance_rate.toFixed(1)}%
                  </p>
                </div>
                <div className={`p-3 rounded-full ${
                  dashboard.overall_compliance_rate >= 80 ? 'bg-green-100' :
                  dashboard.overall_compliance_rate >= 50 ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  {dashboard.overall_compliance_rate >= 50 ? (
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category Filter Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {dashboard.by_category.map(cat => (
            <CategoryCard 
              key={cat.category} 
              category={cat}
              isSelected={categoryFilter === cat.category}
              onSelect={() => setCategoryFilter(categoryFilter === cat.category ? null : cat.category)}
            />
          ))}
        </div>
      )}

      {/* Tabs and Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Compliance Items</CardTitle>
            {categoryFilter && (
              <Badge variant="outline" className="capitalize">
                Filtered: {categoryFilter}
                <button 
                  onClick={() => setCategoryFilter(null)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all" data-testid="tab-all">
                All Items
              </TabsTrigger>
              <TabsTrigger value="critical" data-testid="tab-critical">
                Critical ({dashboard?.critical_count || 0})
              </TabsTrigger>
              <TabsTrigger value="overdue" data-testid="tab-overdue">
                Overdue ({dashboard?.overdue_count || 0})
              </TabsTrigger>
              <TabsTrigger value="due_soon" data-testid="tab-due-soon">
                Due Soon ({dashboard?.due_soon_count || 0})
              </TabsTrigger>
            </TabsList>

            <div className="mt-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  Loading compliance items...
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>No compliance items to display</p>
                </div>
              ) : (
                <div>
                  {items.map(item => (
                    <ComplianceItemCard 
                      key={item.id} 
                      item={item}
                      onAcknowledge={handleAcknowledge}
                      onResolve={handleResolve}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              )}
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
